import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, ArrowRight, ShieldCheck } from 'lucide-react';
import { useStore, vendorStats, vendorUnchecked, nextPaymentForVendor, guardStats, fmtIDR, fmtDate, daysUntil } from '../lib/store';
import { VENDOR_CATEGORIES, MONEY_KEYS, VERIFY_SHORT } from '../lib/checklists';
import { Reveal, StatusChip, Money, EmptyState, Field, inputCls } from '../components/Bits';

export default function VendorPayments() {
  const { state, addPayment } = useStore();
  const [filter, setFilter] = useState('Semua');
  const [showPayForm, setShowPayForm] = useState(false);
  const [f, setF] = useState({ vendorId: '', label: '', amount: '', dueDate: '' });

  const upcoming = state.payments.filter((p) => p.status === 'upcoming').sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const paid = state.payments.filter((p) => p.status === 'paid').sort((a, b) => (a.paidAt > b.paidAt ? -1 : 1));
  const vendorOf = (p) => state.vendors.find((v) => v.id === p.vendorId);
  const nearest = upcoming[0];
  const otherUpcoming = upcoming.slice(1);

  const committed = state.vendors.reduce((a, v) => a + (v.value || 0), 0);
  const paidTotal = paid.reduce((a, p) => a + p.amount, 0);
  const sisaTagihan = Math.max(committed - paidTotal, 0);
  const progress = committed > 0 ? Math.min(paidTotal / committed, 1) : 0;

  const vStats = state.vendors.map((v) => ({ v, s: vendorStats(state, v.id) }));
  const filtered = vStats.filter(({ v }) => filter === 'Semua' || v.category === filter);
  const needVendors = filtered.filter((x) => x.s.remaining > 0).sort((a, b) => b.s.remaining - a.s.remaining);
  const doneVendors = filtered.filter((x) => x.s.remaining === 0);

  const submit = (e) => {
    e.preventDefault();
    if (!f.vendorId || !f.label.trim() || !f.amount || !f.dueDate) return;
    addPayment({ vendorId: f.vendorId, label: f.label.trim(), amount: Number(f.amount), dueDate: f.dueDate });
    setF({ vendorId: '', label: '', amount: '', dueDate: '' });
    setShowPayForm(false);
  };

  const VendorCard = ({ v, s }) => {
    const nextPay = nextPaymentForVendor(state, v.id);
    const gaps = vendorUnchecked(state, v.id)
      .sort((a, b) => {
        const ai = MONEY_KEYS.indexOf(a.key);
        const bi = MONEY_KEYS.indexOf(b.key);
        return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
      })
      .slice(0, 2)
      .map((g) => VERIFY_SHORT[g.key] || g.label.toLowerCase());
    return (
      <Link data-testid={`txn-vendor-card-${v.id}`} to={`/app/vendor/${v.id}`} className="card-lift block rounded-[20px] border border-line bg-white soft-shadow p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-serif text-base font-medium text-ink leading-snug">{v.name}</p>
            <span className="mt-1.5 inline-block rounded-full bg-bone px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink/60">{v.category}</span>
          </div>
          <StatusChip done={s.remaining === 0} />
        </div>
        {v.value > 0 && (
          <p className="mt-3 text-[11px] text-cashmere">
            Nilai kerja sama{' '}<Money className="text-ink font-semibold">{fmtIDR(v.value)}</Money>
          </p>
        )}
        {s.remaining > 0 && (
          <p className="mt-2 text-[11px] leading-relaxed">
            <span className="text-amberw font-semibold">{nextPay ? `Perlu dicek sebelum ${nextPay.label}:` : 'Prioritas utama:'}</span>{' '}
            <span className="text-pewter">{gaps.join(' · ')}</span>
            {s.remaining > 2 && <span className="text-cashmere"> · +{s.remaining - 2} pengecekan lainnya</span>}
          </p>
        )}
        <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-terra">
          Lihat Vendor <ArrowRight size={13} />
        </span>
      </Link>
    );
  };

  return (
    <div data-testid="txn-page" className="space-y-8">
      <Reveal>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-medium tracking-tight text-ink">Vendor & Pembayaran</h1>
            <p className="mt-1.5 text-sm text-cashmere leading-relaxed">Kawal budget dan cek pembayaran sebelum uang berpindah.</p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Link data-testid="txn-add-vendor-btn" to="/app/vendor/baru" className="inline-flex items-center gap-1.5 rounded-full bg-ink text-ivory px-4 py-2.5 text-xs font-bold hover:bg-inkdeep transition-colors">
              <Plus size={14} /> Vendor
            </Link>
            <button data-testid="txn-add-payment-btn" onClick={() => setShowPayForm((s) => !s)} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-white px-4 py-2.5 text-xs font-bold text-ink hover:bg-bone transition-colors">
              {showPayForm ? <X size={14} /> : <Plus size={14} />} Pembayaran
            </button>
          </div>
        </div>
      </Reveal>

      {showPayForm && (
        <form data-testid="txn-add-payment-form" onSubmit={submit} className="rounded-[20px] border border-line bg-white soft-shadow p-5 space-y-4">
          <Field label="Vendor">
            <select data-testid="payment-vendor-select" className={inputCls} value={f.vendorId} onChange={(e) => setF({ ...f, vendorId: e.target.value })} required>
              <option value="">Pilih vendor</option>
              {state.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
          <Field label="Tahap pembayaran">
            <input data-testid="payment-label-input" className={inputCls} placeholder="DP Tahap 2" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} required />
          </Field>
          <Field label="Jumlah (Rp)">
            <input data-testid="payment-amount-input" type="number" min="0" className={inputCls} placeholder="15000000" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required />
          </Field>
          <Field label="Tanggal jatuh tempo">
            <input data-testid="payment-date-input" type="date" className={inputCls} value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} required />
          </Field>
          <button data-testid="payment-save-btn" type="submit" className="w-full rounded-full bg-ink text-ivory py-3.5 text-sm font-bold">Simpan Pembayaran</button>
        </form>
      )}

      {/* SECTION 1 — SEBELUM TRANSFER */}
      <Reveal delay={0.05}>
        <section>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-3">Pembayaran Terdekat</p>
          {!nearest ? (
            <p className="text-sm text-cashmere rounded-[20px] border border-line bg-white soft-shadow px-5 py-5">Semua pembayaran terkendali saat ini.</p>
          ) : (
            <div data-testid={`txn-nearest-${nearest.id}`} className="rounded-[20px] border border-line bg-white soft-shadow p-5">
              <p className="text-[11px] font-semibold text-cashmere">{vendorOf(nearest)?.name}</p>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <p className="text-sm font-bold text-ink">{nearest.label}</p>
                <Money className="text-xl font-semibold text-ink">{fmtIDR(nearest.amount)}</Money>
              </div>
              <p className="font-mono tnum text-[11px] text-cashmere mt-1">
                {fmtDate(nearest.dueDate)}{daysUntil(nearest.dueDate) >= 0 ? ` · ${daysUntil(nearest.dueDate)} hari lagi` : ''}
              </p>
              <div className="mt-3.5 flex items-center justify-between gap-3">
                {(() => {
                  const g = guardStats(state, nearest.id);
                  return g.remaining > 0 ? (
                    <StatusChip done={false} todoLabel={`${g.remaining} hal perlu dicek`} />
                  ) : (
                    <StatusChip done doneLabel="Checklist utama sudah ditinjau" />
                  );
                })()}
                <Link data-testid={`txn-guard-cta-${nearest.id}`} to={`/app/pembayaran/${nearest.id}/guard`} className="inline-flex items-center gap-1.5 rounded-full bg-terra text-ivory px-4 py-2.5 text-[11px] font-bold hover:brightness-110 transition shrink-0">
                  <ShieldCheck size={13} /> Cek Sebelum Transfer
                </Link>
              </div>
            </div>
          )}
          {otherUpcoming.length > 0 && (
            <div className="mt-3 space-y-2">
              {otherUpcoming.map((p) => {
                const g = guardStats(state, p.id);
                return (
                  <Link data-testid={`txn-payment-${p.id}`} key={p.id} to={`/app/pembayaran/${p.id}/guard`} className="card-lift flex items-center justify-between gap-3 rounded-xl border border-line bg-white px-4 py-3.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{p.label} · {vendorOf(p)?.name}</p>
                      <p className="font-mono tnum text-[11px] text-cashmere mt-0.5">{fmtIDR(p.amount)} · {fmtDate(p.dueDate)}</p>
                    </div>
                    {g.remaining > 0 ? <StatusChip done={false} todoLabel={`${g.remaining} perlu dicek`} /> : <StatusChip done doneLabel="Sudah ditinjau" />}
                  </Link>
                );
              })}
            </div>
          )}
          {paid.length > 0 && (
            <div className="mt-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cashmere mb-2">Sudah Dibayar</p>
              <div className="space-y-2">
                {paid.map((p) => (
                  <div data-testid={`txn-paid-${p.id}`} key={p.id} className="rounded-xl border border-[#CBDCCE] bg-[#EBF2EC]/40 px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{p.label} · {vendorOf(p)?.name}</p>
                      <p className="font-mono tnum text-[10px] text-cashmere mt-0.5">{fmtDate(p.paidAt)}{p.reference ? ` · Ref ${p.reference}` : ''}</p>
                    </div>
                    <Money className="text-sm font-semibold text-sage shrink-0">{fmtIDR(p.amount)}</Money>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </Reveal>

      {/* SECTION 2 — BUDGET RINGKAS */}
      {(committed > 0 || state.profile.budget > 0) && (
        <Reveal delay={0.08}>
          <section data-testid="txn-budget" className="rounded-[20px] border border-line bg-white soft-shadow p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-4">Budget Ringkas</p>
            <div className="space-y-2.5">
              {state.profile.budget > 0 && (
                <div className="flex items-baseline justify-between">
                  <p className="text-sm text-cashmere">Total Budget</p>
                  <Money data-testid="budget-total" className="text-sm font-semibold text-ink">{fmtIDR(state.profile.budget)}</Money>
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <p className="text-sm text-cashmere">Committed</p>
                <Money data-testid="budget-committed" className="text-sm font-semibold text-ink">{fmtIDR(committed)}</Money>
              </div>
              <div className="flex items-baseline justify-between">
                <p className="text-sm text-cashmere">Sudah Dibayar</p>
                <Money data-testid="budget-paid" className="text-sm font-semibold text-sage">{fmtIDR(paidTotal)}</Money>
              </div>
              <div className="flex items-baseline justify-between">
                <p className="text-sm text-cashmere">Sisa Tagihan</p>
                <Money data-testid="budget-due" className="text-sm font-semibold text-ink">{fmtIDR(sisaTagihan)}</Money>
              </div>
            </div>
            {committed > 0 && (
              <div className="mt-4 h-1.5 rounded-full bg-bone overflow-hidden">
                <div data-testid="budget-progress" className="h-full rounded-full bg-sage/70 transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            )}
          </section>
        </Reveal>
      )}

      {/* SECTION 3 — VENDOR */}
      <Reveal delay={0.1}>
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra">
              {needVendors.length > 0 ? 'Vendor yang Perlu Dicek' : 'Semua Vendor'}
            </p>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 pb-1 mb-3">
            {['Semua', ...VENDOR_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`txn-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => setFilter(c)}
                className={`shrink-0 rounded-full px-3.5 py-2 text-[11px] font-semibold border transition-colors ${
                  filter === c ? 'bg-ink text-ivory border-ink' : 'bg-white text-cashmere border-line hover:border-ink/30'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          {state.vendors.length === 0 ? (
            <EmptyState title="Belum ada vendor yang ditambahkan" desc="Mulai dari yang paling krusial seperti venue atau katering.">
              <Link data-testid="txn-empty-add" to="/app/vendor/baru" className="inline-flex rounded-full bg-ink text-ivory px-5 py-2.5 text-xs font-bold">+ Tambah Vendor</Link>
            </EmptyState>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-cashmere rounded-[20px] border border-line bg-white px-5 py-5">Tidak ada vendor pada kategori ini.</p>
          ) : (
            <div className="space-y-3">
              {needVendors.map(({ v, s }) => <VendorCard key={v.id} v={v} s={s} />)}
              {doneVendors.length > 0 && needVendors.length > 0 && (
                <p className="pt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-cashmere">Sudah Dicek</p>
              )}
              {doneVendors.map(({ v, s }) => <VendorCard key={v.id} v={v} s={s} />)}
            </div>
          )}
        </section>
      </Reveal>
    </div>
  );
}
